import { Airport, FlightData, FlightId, FlightPosition, LatLng } from '../types'

export function parseFlight (xml: XMLDocument): FlightData[] {
  const error = xml.getElementsByTagName('Error')
  if (error.length > 0) {
    return []
  }
  const result = xml.getElementsByTagName('ResultStatus')
  if (result.length > 0 && result[0].getElementsByTagName('OK').length === 0) {
    return []
  }
  const flights = xml.getElementsByTagName('Flight')
  return Array.from(flights).map((flight) => {
    return {
      id: flight.getAttribute('FlightId'),
      departureAirport: getAirport(flight.getElementsByTagName('Departure')),
      departureAirports: getAirports(flight.getElementsByTagName('Departure')),
      arrivalAirport: getAirport(flight.getElementsByTagName('Arrival')),
      arrivalAirports: getAirports(flight.getElementsByTagName('Arrival')),
      status: getStatuses(flight),
      remaining: getRemainingTime(flight),
      flightId: getFlightId(flight),
      flightInfo: {
        aircraftType: getFirstNodeValue(flight, 'AircraftType'),
        registration: getFirstNodeValue(flight, 'TailNumber'),
        altitude: parseInt(getFirstNodeValue(flight, 'Altitude')),
        speed: parseInt(getFirstNodeValue(flight, 'Speed'))
      },
      route: getRouteCoordinates(flight),
      position: getFlightPosition(flight),
      numLegs: getFirstNodeValue(flight, 'NumLegs')
    }
  })
}

function getFlightId (flight: Element): FlightId {
  const flightId = flight.getElementsByTagName('FlightId')[0]
  const codeShare = flightId.getElementsByTagName('CodeShare').length === 1 ? flightId.getElementsByTagName('CodeShare')[0] : null
  const regional = flightId.getElementsByTagName('RegionalCarrier').length === 1 ? flightId.getElementsByTagName('RegionalCarrier')[0] : null
  return {
    flightNumber: getFirstNodeValue(flight, 'FlightNumber'),
    airlineCode: getFirstNodeValue(flight, 'AirlineCode'),
    airlineName: getFirstNodeValue(flight, 'AirlineName'),
    codeShare: codeShare
      ? {
          flightNumber: getFirstNodeValue(codeShare, 'FlightNumber'),
          airlineCode: getFirstNodeValue(codeShare, 'AirlineCode'),
          airlineName: getFirstNodeValue(codeShare, 'AirlineName')
        }
      : null,
    regionalCarrier: regional
      ? {
          flightNumber: getFirstNodeValue(regional, 'FlightNumber'),
          airlineCode: getFirstNodeValue(regional, 'AirlineCode'),
          airlineName: getFirstNodeValue(regional, 'AirlineName')
        }
      : null
  }
}

function getStatuses (element: Element): string[] {
  const flightStatus = element.getElementsByTagName('FlightStatus')
  if (flightStatus.length > 0) {
    return Array.from(flightStatus[0].childNodes).map((status) => status.nodeName)
  } else {
    return []
  }
}

function getRemainingTime (xml: Element): string {
  const remaining = xml.getElementsByTagName('RelativeTime')
  if (remaining.length > 0) {
    const time = remaining[0].getElementsByTagName('Time')
    if (time.length > 0) {
      return time[0].firstChild.nodeValue
    }
  }
  return null
}

function getAirports (airports: HTMLCollectionOf<Element>): Airport[] {
  return Array.from(airports).map((node) => convertAirport(node))
}

function getAirport (airports: HTMLCollectionOf<Element>): Airport {
  const node = airports[airports.length - 1]
  return convertAirport(node)
}

function convertAirport (node: Element): Airport {
  const scheduled = getTime(node, 'Scheduled')
  const actual = getTime(node, 'Actual')
  const estimated = getTime(node, 'Estimated')
  return {
    code: getUniqueNodeValue(node, 'AirportCode'),
    name: getUniqueNodeValue(node, 'CityName'),
    stateId: getUniqueNodeValue(node, 'StateId'),
    countryId: getUniqueNodeValue(node, 'CountryId'),
    terminal: getUniqueNodeValue(node, 'Terminal'),
    gate: getUniqueNodeValue(node, 'Gate'),
    baggage: getUniqueNodeValue(node, 'Baggage'),
    scheduled: scheduled[0],
    actual: actual[0],
    estimated: estimated[0],
    time: actual[0] || estimated[0] || scheduled[0],
    utc: {
      scheduled: scheduled[1],
      actual: actual[1],
      estimated: estimated[1],
      time: actual[1] || estimated[1] || scheduled[1]
    }
  }
}

function getRouteCoordinates (xml: Element): LatLng[] {
  const route = xml.getElementsByTagName('Route')
  if (route.length === 0) return []

  const coordinates = route[0].getElementsByTagName('Coordinates')
  const results: LatLng[] = []
  for (let i = 0; i < coordinates.length; i++) {
    const coord = coordinates[i]
    results.push({ lat: parseLatitude(coord), lng: parseLongitude(coord) })
  }
  return results
}

function getFlightPosition (xml: Element): FlightPosition {
  const result: Partial<FlightPosition> = {}
  const airborne = xml.getElementsByTagName('Airborne')
  if (airborne.length === 0) return null

  const coordinates = airborne[0].getElementsByTagName('Coordinates')
  if (coordinates.length === 1) {
    result.lat = parseLatitude(coordinates[0])
    result.lng = parseLongitude(coordinates[0])
  }

  result.altitude = parseInt(getUniqueNodeValue(airborne[0], 'Altitude'))
  result.speed = parseInt(getUniqueNodeValue(airborne[0], 'Speed'))
  result.heading = parseInt(getUniqueNodeValue(airborne[0], 'Heading'))
  return result
}

function parseLatitude (coordinates: Element): number {
  const latitude = coordinates.getElementsByTagName('Latitude')[0]
  const angle = latitude.getElementsByTagName('Angle')[0]
  const direction = latitude.getElementsByTagName('South').length === 0 ? 1 : -1
  return parseFloat(angle.firstChild.nodeValue) * direction
}

function parseLongitude (coordinates: Element): number {
  const longitude = coordinates.getElementsByTagName('Longitude')[0]
  const angle = longitude.getElementsByTagName('Angle')[0]
  const direction = longitude.getElementsByTagName('West').length === 0 ? 1 : -1
  return parseFloat(angle.firstChild.nodeValue) * direction
}

function getUniqueNodeValue (xml: Element | XMLDocument, nodeName: string): string {
  const elements = xml.getElementsByTagName(nodeName)
  if (elements && elements.length === 1) {
    return elements[0].firstChild.nodeValue
  } else {
    return null
  }
}

function getFirstNodeValue (xml: Element | XMLDocument, nodeName: string): string {
  const elements = xml.getElementsByTagName(nodeName)
  if (elements && elements.length >= 1) {
    return elements[0].firstChild.nodeValue
  } else {
    return null
  }
}

function getTime (node: Element, type: string): [Date, Date] {
  const elements = node.getElementsByTagName('DateTime')
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i]
    const match = element.getElementsByTagName(type)
    if (match.length === 1) {
      return extractDateTime(element)
    }
  }
  return [null, null]
}

function extractDateTime (element: Element): [Date, Date] {
  const time = element.getElementsByTagName('Time')
  const date = element.getElementsByTagName('Date')
  if (time.length === 1 && date.length === 1) {
    const localTime = time[0].firstChild.nodeValue
    const localDate = date[0].firstChild.nodeValue
    const utcTime = time[0].getAttribute('utc')
    const utcDate = date[0].getAttribute('utc')
    return [new Date(`${localDate}T${localTime}`), new Date(`${utcDate}T${utcTime}+00:00`)]
  }
  return [null, null]
}
